/**
 * Analytics 工具函数
 * 用于统一管理 Google Analytics 和 Microsoft Clarity 事件上报
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void
    clarity?: (...args: any[]) => void
  }
}

const AI_REFERRERS: Array<[domain: string, source: string]> = [
  ['chatgpt.com', 'chatgpt'],
  ['chat.openai.com', 'chatgpt'],
  ['perplexity.ai', 'perplexity'],
  ['claude.ai', 'claude'],
  ['gemini.google.com', 'gemini'],
  ['copilot.microsoft.com', 'copilot'],
  ['poe.com', 'poe'],
  ['you.com', 'you'],
  ['phind.com', 'phind'],
  ['deepseek.com', 'deepseek'],
  ['grok.com', 'grok'],
  ['ai-bot.cn', 'ai-bot'],
]

export function getAIReferrerSource(referrer: string): string | null {
  if (!referrer) return null
  try {
    const hostname = new URL(referrer).hostname.toLowerCase()
    return AI_REFERRERS.find(([domain]) => hostname === domain || hostname.endsWith(`.${domain}`))?.[1] || null
  } catch {
    return null
  }
}

const normalizeClarityValue = (value: any): string | string[] => {
  if (Array.isArray(value)) {
    return value.map(item => normalizeClarityValue(item) as string).filter(Boolean).slice(0, 20)
  }

  if (value === null || typeof value === 'undefined') return ''

  if (typeof value === 'object') {
    return JSON.stringify(value).slice(0, 255)
  }

  return String(value).slice(0, 255)
}

const setClarityTags = (metadata?: Record<string, any>) => {
  if (!metadata || typeof window === 'undefined' || !window.clarity) return

  Object.entries(metadata).slice(0, 64).forEach(([key, value]) => {
    const normalizedKey = key.replace(/[^\w -]/g, '_').slice(0, 255)
    const normalizedValue = normalizeClarityValue(value)
    if (!normalizedKey || !normalizedValue || (Array.isArray(normalizedValue) && normalizedValue.length === 0)) return

    window.clarity?.('set', normalizedKey, normalizedValue)
  })
}

/**
 * 上报 Clarity 自定义事件
 */
export function trackClarityEvent(eventName: string, metadata?: Record<string, any>) {
  if (typeof window !== 'undefined' && window.clarity) {
    try {
      setClarityTags(metadata)
      window.clarity('event', eventName)
    } catch (error) {
      console.error('[Clarity] Failed to track event:', eventName, error)
    }
  }
}

/**
 * 上报 Google Analytics 事件
 */
export function trackGAEvent(eventName: string, params?: Record<string, any>) {
  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', eventName, params)
    } catch (error) {
      console.error('[GA] Failed to track event:', eventName, error)
    }
  }
}

/**
 * 统一事件上报：GA4 + Clarity + dev console fallback
 */
export function trackEvent(eventName: string, params?: Record<string, any>) {
  if (typeof window === 'undefined') return

  const hasGA = !!window.gtag
  const hasClarity = !!window.clarity

  if (hasClarity) trackClarityEvent(eventName, params)
  if (hasGA) trackGAEvent(eventName, params)

  if (!hasGA && !hasClarity && process.env.NODE_ENV !== 'production') {
    console.log(`[Analytics:dev] ${eventName}`, params || '')
  }
}
